import React, { useCallback, useEffect, useMemo, useState } from "react";
import GenericModal from "./GenericModal";
import useModalStore from "../../stores/modalStore";
import useUserStore from "../../stores/userStore";
import { fetchDailySummaryRange, fetchSickHoursRange } from "../../services/eventService";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer,
} from "recharts";
import { AlertCircle, Loader2 } from "lucide-react";

const COLORS = ["#94a3b8", "#cbd5e1", "#a8a29e", "#d6d3d1", "#9ca3af", "#d1d5db", "#b6bbc4", "#c4b5fd"];
const SELECTED_COLOR = "#4f46e5";
const PROJECTION_COLOR = "#f59e0b";

const toISODate = (date) => date.toISOString().split("T")[0];
const parseLocalDate = (date) => new Date(`${date}T12:00:00`);
const firstNameKey = (name) => String(name || "").trim().split(/\s+/)[0].toLowerCase();
const nameKey = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
const round = (value) => Math.round((Number(value) || 0) * 100) / 100;
const formatDate = (date) => parseLocalDate(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatMoneyWithCents = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildDateRange = (startDate, endDate) => {
    const dates = [];
    let cursor = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    while (cursor <= end) {
        dates.push(toISODate(cursor));
        cursor = new Date(cursor.getTime() + 86400000);
    }
    return dates;
};

const CustomTooltip = ({ active, payload, label, isMoney = false, valueFormatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs min-w-[190px]">
            <div className="font-semibold text-gray-700 mb-1.5">{formatDate(label)}</div>
            {payload
                .filter((p) => p.value != null)
                .map((p) => (
                    <div key={p.dataKey} className="flex justify-between gap-4">
                        <span style={{ color: p.color }}>{p.name || p.dataKey}</span>
                        <span className="font-medium text-gray-800">
                            {valueFormatter ? valueFormatter(p.value) : isMoney ? formatMoney(p.value) : Number(p.value || 0).toFixed(1)}
                        </span>
                    </div>
                ))}
        </div>
    );
};

const MetricChart = ({ title, data, employees, selectedEmployee, dataKey, projectionKey, isMoney = false, valueFormatter, tickFormatter }) => (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
        <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#94a3b8" }} interval={Math.max(0, Math.floor(data.length / 8))} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={tickFormatter || ((v) => isMoney ? `$${Math.round(v / 1000)}k` : v)} />
                    <Tooltip content={<CustomTooltip isMoney={isMoney} valueFormatter={valueFormatter} />} />
                    <Legend wrapperStyle={{ fontSize: "11px" }} />
                    {employees.map((employee, index) => {
                        const isSelected = employee.name === selectedEmployee;
                        const key = `${employee.key}_${dataKey}`;
                        return (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                name={isSelected ? employee.name : employee.label}
                                stroke={isSelected ? SELECTED_COLOR : COLORS[index % COLORS.length]}
                                strokeWidth={isSelected ? 3 : 1.5}
                                dot={false}
                                opacity={isSelected ? 1 : 0.45}
                                connectNulls
                            />
                        );
                    })}
                    {projectionKey && (
                        <Line
                            type="monotone"
                            dataKey={projectionKey}
                            name="Projected income"
                            stroke={PROJECTION_COLOR}
                            strokeWidth={2.5}
                            strokeDasharray="6 4"
                            dot={false}
                            connectNulls
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const EmployeeComparisonModal = () => {
    const showEmployeeComparisonModal = useModalStore((s) => s.showEmployeeComparisonModal);
    const { currentUser, employees: storeEmployees } = useUserStore();

    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [trendData, setTrendData] = useState([]);
    const [trendEmployees, setTrendEmployees] = useState([]);
    const [sickRows, setSickRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;
    const todayIso = toISODate(new Date());
    const actualEnd = year === currentYear ? todayIso : yearEnd;

    const loadData = useCallback(async () => {
        if (!currentUser?.user_id) return;
        setLoading(true);
        setError(null);
        try {
            const [summary, sick] = await Promise.all([
                fetchDailySummaryRange(yearStart, actualEnd, currentUser.user_id),
                fetchSickHoursRange(yearStart, actualEnd),
            ]);
            const names = summary.employees || [];
            setTrendData(summary.data || []);
            setTrendEmployees(names);
            setSickRows(Array.isArray(sick) ? sick : []);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Failed to load employee comparison data");
        } finally {
            setLoading(false);
        }
    }, [actualEnd, currentUser?.user_id, yearStart]);

    useEffect(() => {
        if (showEmployeeComparisonModal) {
            loadData();
        }
    }, [showEmployeeComparisonModal, loadData]);

    const userIdToFirstName = useMemo(() => {
        const map = new Map();
        for (const employee of storeEmployees || []) {
            map.set(Number(employee.user_id), String(employee.first_name || "").toLowerCase());
        }
        return map;
    }, [storeEmployees]);

    const canonicalByNameKey = useMemo(() => {
        const map = new Map();
        const rawNames = new Set(trendEmployees.filter(Boolean));
        for (const point of trendData || []) {
            for (const rawName of Object.keys(point.by_employee || {})) {
                rawNames.add(rawName);
            }
        }

        const rawNameGroups = {};
        for (const rawName of rawNames) {
            const first = firstNameKey(rawName);
            if (!first) continue;
            if (!rawNameGroups[first]) rawNameGroups[first] = [];
            rawNameGroups[first].push(String(rawName).trim().replace(/\s+/g, " "));
        }

        for (const names of Object.values(rawNameGroups)) {
            const uniqueNames = Array.from(new Set(names));
            const byLastToken = {};
            for (const name of uniqueNames) {
                const parts = name.trim().split(/\s+/);
                if (parts.length < 2) continue;
                const last = parts[parts.length - 1].toLowerCase();
                if (!byLastToken[last]) byLastToken[last] = [];
                byLastToken[last].push(name);
            }
            for (const sameLastNames of Object.values(byLastToken)) {
                if (sameLastNames.length < 2) continue;
                const canonical = sameLastNames.sort((a, b) => b.length - a.length)[0];
                for (const name of sameLastNames) {
                    map.set(nameKey(name), canonical);
                }
            }

            const fullName = uniqueNames
                .filter((name) => name.trim().split(/\s+/).length > 1)
                .sort((a, b) => b.length - a.length)[0];
            if (fullName) {
                for (const name of uniqueNames) {
                    map.set(nameKey(name), fullName);
                }
            }
        }

        const firstNameCounts = {};
        for (const employee of storeEmployees || []) {
            const firstName = String(employee.first_name || "").trim();
            if (!firstName) continue;
            const key = nameKey(firstName);
            firstNameCounts[key] = (firstNameCounts[key] || 0) + 1;
        }
        for (const employee of storeEmployees || []) {
            const firstName = String(employee.first_name || "").trim();
            const preferredFirstName = String(employee.preferred_first_name || "").trim();
            const lastName = String(employee.last_name || "").trim();
            const alternateLastName = String(employee.alternate_last_name || "").trim();
            if (!firstName) continue;
            const displayName = `${firstName} ${lastName}`.trim();
            const hasFullName = displayName.trim().split(/\s+/).length > 1;
            if (hasFullName) {
                map.set(nameKey(displayName), displayName);
                if (preferredFirstName) {
                    map.set(nameKey(`${preferredFirstName} ${lastName}`), displayName);
                    map.set(nameKey(preferredFirstName), displayName);
                }
                if (alternateLastName) {
                    map.set(nameKey(`${firstName} ${alternateLastName}`), displayName);
                    if (preferredFirstName) {
                        map.set(nameKey(`${preferredFirstName} ${alternateLastName}`), displayName);
                    }
                }
            }
            if (firstNameCounts[nameKey(firstName)] === 1 && (hasFullName || !map.has(nameKey(firstName)))) {
                map.set(nameKey(firstName), displayName);
            }
        }
        return map;
    }, [storeEmployees, trendData, trendEmployees]);

    const canonicalizeEmployeeName = useCallback((name) => {
        const trimmed = String(name || "").trim().replace(/\s+/g, " ");
        return canonicalByNameKey.get(nameKey(trimmed)) || trimmed;
    }, [canonicalByNameKey]);

    const displayEmployees = useMemo(() => {
        return Array.from(new Set(trendEmployees.map(canonicalizeEmployeeName).filter(Boolean))).sort();
    }, [canonicalizeEmployeeName, trendEmployees]);

    const normalizedTrendData = useMemo(() => {
        return trendData.map((point) => {
            const byEmployee = {};
            for (const [rawName, metrics] of Object.entries(point.by_employee || {})) {
                const canonicalName = canonicalizeEmployeeName(rawName);
                if (!canonicalName) continue;
                if (!byEmployee[canonicalName]) {
                    byEmployee[canonicalName] = {};
                }
                for (const [metricKey, metricValue] of Object.entries(metrics || {})) {
                    byEmployee[canonicalName][metricKey] = round((byEmployee[canonicalName][metricKey] || 0) + Number(metricValue || 0));
                }
            }
            return { ...point, by_employee: byEmployee };
        });
    }, [canonicalizeEmployeeName, trendData]);

    useEffect(() => {
        setSelectedEmployee((previous) => previous && displayEmployees.includes(previous) ? previous : (displayEmployees[0] || ""));
    }, [displayEmployees]);

    const sickByEmployeeDate = useMemo(() => {
        const output = {};
        for (const row of sickRows) {
            const firstName = userIdToFirstName.get(Number(row.user_id));
            const date = String(row.work_date || "").slice(0, 10);
            if (!firstName || !date) continue;
            if (!output[firstName]) output[firstName] = {};
            output[firstName][date] = (output[firstName][date] || 0) + Number(row.sick_hours || 0);
        }
        return output;
    }, [sickRows, userIdToFirstName]);

    const chartEmployees = useMemo(() => {
        const selected = displayEmployees.includes(selectedEmployee) ? [selectedEmployee] : [];
        const peers = displayEmployees.filter((name) => name !== selectedEmployee);
        return [...selected, ...peers].map((name, index) => ({
            name,
            key: `emp${index}`,
            label: index === 0 ? name : `Peer ${index}`,
        }));
    }, [displayEmployees, selectedEmployee]);

    const chartData = useMemo(() => {
        if (!normalizedTrendData.length || !chartEmployees.length) return [];

        const byDate = Object.fromEntries(normalizedTrendData.map((row) => [row.date, row]));
        const cumulative = {};
        for (const employee of chartEmployees) {
            cumulative[employee.name] = { hours: 0, sick: 0, income: 0, revenue: 0 };
        }

        const rows = buildDateRange(yearStart, yearEnd).map((date) => {
            const isFuture = date > actualEnd;
            const point = byDate[date] || {};
            const row = { date };

            for (const employee of chartEmployees) {
                const metrics = point.by_employee?.[employee.name] || {};
                const sickHours = sickByEmployeeDate[firstNameKey(employee.name)]?.[date] || 0;
                if (!isFuture) {
                    cumulative[employee.name].hours += Number(metrics.hours || 0);
                    cumulative[employee.name].sick += sickHours;
                    cumulative[employee.name].income += Number(metrics.total_income || 0);
                    cumulative[employee.name].revenue += Number(metrics.revenue || 0);
                }

                row[`${employee.key}_hours`] = isFuture ? null : round(cumulative[employee.name].hours);
                row[`${employee.key}_sick`] = isFuture ? null : round(cumulative[employee.name].sick);
                row[`${employee.key}_income`] = isFuture ? null : round(cumulative[employee.name].income);
                row[`${employee.key}_serviceRate`] = isFuture || cumulative[employee.name].hours <= 0 ? null : round(cumulative[employee.name].revenue / cumulative[employee.name].hours);
            }
            return row;
        });

        const selected = chartEmployees[0];
        if (selected) {
            const selectedIncomeKey = `${selected.key}_income`;
            const projectionKey = `${selected.key}_income_projection`;
            let lastActualIndex = -1;
            for (let i = rows.length - 1; i >= 0; i--) {
                if (rows[i].date <= actualEnd && rows[i][selectedIncomeKey] != null) {
                    lastActualIndex = i;
                    break;
                }
            }
            if (lastActualIndex >= 0 && actualEnd < yearEnd) {
                const lastActual = rows[lastActualIndex][selectedIncomeKey] || 0;
                const elapsedDays = Math.max(1, lastActualIndex + 1);
                const dailyRate = lastActual / elapsedDays;
                rows[lastActualIndex][projectionKey] = round(lastActual);
                for (let i = lastActualIndex + 1; i < rows.length; i++) {
                    rows[i][projectionKey] = round(dailyRate * (i + 1));
                }
            }
        }

        return rows;
    }, [actualEnd, chartEmployees, normalizedTrendData, sickByEmployeeDate, yearEnd, yearStart]);

    const selectedSummary = useMemo(() => {
        if (!selectedEmployee || !chartData.length || !chartEmployees.length) return null;
        const selected = chartEmployees[0];
        const lastActual = [...chartData].reverse().find((row) => row.date <= actualEnd);
        const projected = [...chartData].reverse().find((row) => row[`${selected.key}_income_projection`] != null);
        return {
            hours: lastActual?.[`${selected.key}_hours`] || 0,
            sick: lastActual?.[`${selected.key}_sick`] || 0,
            income: lastActual?.[`${selected.key}_income`] || 0,
            serviceRate: lastActual?.[`${selected.key}_serviceRate`] || 0,
            projectedIncome: projected?.[`${selected.key}_income_projection`] || lastActual?.[`${selected.key}_income`] || 0,
        };
    }, [actualEnd, chartData, chartEmployees, selectedEmployee]);

    const projectionKey = chartEmployees[0] ? `${chartEmployees[0].key}_income_projection` : null;

    return (
        <GenericModal
            name="employeeComparisonModal"
            title="Employee Comparison"
            hideButton={true}
            modalMaxWidth="max-w-6xl"
        >
            <div className="p-5 space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Employee</label>
                        <select
                            value={selectedEmployee}
                            onChange={(e) => setSelectedEmployee(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                        >
                            {displayEmployees.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Year</label>
                        <select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                        >
                            {Array.from({ length: currentYear - 2024 }, (_, i) => 2025 + i).map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="px-3 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                    <div className="text-xs text-gray-400 max-w-xl">
                        Peers are anonymized. Values are cumulative for the selected year. Service rate is YTD service revenue divided by YTD hours worked. Projected income uses the selected employee's current year-to-date daily income pace.
                    </div>
                </div>

                {selectedSummary && !loading && !error && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                            <div className="text-xs font-semibold text-blue-600">YTD Hours</div>
                            <div className="text-lg font-bold text-blue-700">{selectedSummary.hours.toFixed(1)}</div>
                        </div>
                        <div className="rounded-xl border border-rose-100 bg-rose-50 p-3">
                            <div className="text-xs font-semibold text-rose-600">YTD Sick Hours</div>
                            <div className="text-lg font-bold text-rose-700">{selectedSummary.sick.toFixed(1)}</div>
                        </div>
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                            <div className="text-xs font-semibold text-emerald-600">YTD Income</div>
                            <div className="text-lg font-bold text-emerald-700">{formatMoney(selectedSummary.income)}</div>
                        </div>
                        <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-3">
                            <div className="text-xs font-semibold text-cyan-600">YTD Service Rate</div>
                            <div className="text-lg font-bold text-cyan-700">{formatMoneyWithCents(selectedSummary.serviceRate)}/hr</div>
                        </div>
                        <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
                            <div className="text-xs font-semibold text-amber-600">Projected Year-End Income</div>
                            <div className="text-lg font-bold text-amber-700">{formatMoney(selectedSummary.projectedIncome)}</div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-indigo-500" size={32} />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center gap-2 py-20 text-red-500">
                        <AlertCircle size={18} />
                        <span className="text-sm">{error}</span>
                    </div>
                ) : chartData.length === 0 ? (
                    <div className="text-center py-20 text-gray-400 text-sm">No comparison data available</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <MetricChart title="Cumulative Hours Worked" data={chartData} employees={chartEmployees} selectedEmployee={selectedEmployee} dataKey="hours" />
                        <MetricChart title="Cumulative Sick Hours" data={chartData} employees={chartEmployees} selectedEmployee={selectedEmployee} dataKey="sick" />
                        <MetricChart title="Cumulative Total Income" data={chartData} employees={chartEmployees} selectedEmployee={selectedEmployee} dataKey="income" projectionKey={projectionKey} isMoney />
                        <MetricChart title="YTD Service Rate" data={chartData} employees={chartEmployees} selectedEmployee={selectedEmployee} dataKey="serviceRate" valueFormatter={(value) => `${formatMoneyWithCents(value)}/hr`} tickFormatter={(value) => `$${Number(value || 0).toFixed(0)}`} />
                    </div>
                )}
            </div>
        </GenericModal>
    );
};

export default EmployeeComparisonModal;
